import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { Eye, AlertTriangle, ShieldCheck, Play, Clock, ExternalLink } from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { BackToDashboard } from "@/components/rawan/BackToDashboard";
import { useAuth } from "@/lib/auth";
import { decryptUrl, decryptInnerPayload } from "@/lib/safelink-crypto";
import { logSafelink } from "@/lib/safelink-log";

const search = z.object({
  t: z.string().optional(),
  kind: z.enum(["movie", "series", "trailer"]).optional(),
});

export const Route = createFileRoute("/admin/preview")({
  validateSearch: search,
  head: () => ({
    meta: [
      { title: "معاينة الرابط المحمي — Rawan UI" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PreviewAdminPage,
});

function maskUrl(u: string): string {
  try {
    const url = new URL(u);
    const path = url.pathname;
    const shortPath = path.length > 24 ? path.slice(0, 12) + "…" + path.slice(-8) : path;
    return `${url.protocol}//${url.hostname}${shortPath}`;
  } catch {
    return u.length > 40 ? u.slice(0, 24) + "…" + u.slice(-12) : u;
  }
}

interface DecodedPayload {
  url: string;
  kind?: string;
  slug?: string;
  title?: string;
  exp?: number;
  iat?: number;
  expired: boolean;
}

function PreviewAdminPage() {
  const { isAuthed, ready, email } = useAuth();
  const navigate = useNavigate();
  const { t: token, kind } = Route.useSearch();
  const [pasted, setPasted] = useState("");
  const [data, setData] = useState<DecodedPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && !isAuthed) navigate({ to: "/login" });
  }, [ready, isAuthed, navigate]);

  async function runPreview(input: string) {
    setLoading(true);
    setErr(null);
    setData(null);
    try {
      const raw = input.trim();
      let extracted = raw;
      try {
        const u = new URL(raw);
        const t = u.searchParams.get("t");
        if (t) extracted = t;
      } catch { /* not a URL, treat as raw token */ }

      let payload: DecodedPayload;
      try {
        const inner = await decryptUrl(extracted);
        payload = await decryptInnerPayload(inner);
      } catch {
        // ربما token طبقة واحدة فقط
        const real = await decryptUrl(extracted);
        payload = { url: real, expired: false };
      }
      setData(payload);
      logSafelink({
        source: "admin",
        status: payload.expired ? "expired" : "success",
        kind: payload.kind ?? kind,
        slug: payload.slug,
        userId: email ?? null,
        note: "preview",
      });
    } catch {
      setErr("تعذّر فك تشفير الرابط — تأكّد من نسخ الرابط/التوكن كاملاً.");
      logSafelink({ source: "admin", status: "decrypt-failed", kind, userId: email ?? null, note: "preview-failed" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (token) runPreview(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!ready || !isAuthed) return null;

  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-3xl">
        <BackToDashboard className="mb-4" />

        <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="grid place-items-center w-12 h-12 rounded-2xl" style={{ background: "var(--gradient-primary)" }}>
              <Eye className="h-6 w-6 text-primary-foreground" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-extrabold">معاينة الرابط المحمي</h1>
              <p className="text-xs text-muted-foreground">افحص بأمان ما سيُشغّله الرابط قبل فتحه فعلياً</p>
            </div>
          </div>

          <label className="block text-sm font-bold mb-1.5">الرابط المحمي أو التوكن</label>
          <textarea
            rows={3}
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            placeholder="الصق هنا الرابط الكامل /play?t=... أو التوكن المشفر مباشرة"
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <div className="mt-2 flex gap-2">
            <button
              disabled={!pasted.trim() || loading}
              onClick={() => runPreview(pasted)}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              <ShieldCheck className="h-4 w-4" />
              {loading ? "جارٍ فحص الرابط…" : "افحص الرابط بأمان"}
            </button>
          </div>

          {err && (
            <div className="mt-5 flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {err}
            </div>
          )}

          {data && (
            <div className="mt-6 space-y-4">
              {data.expired ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/15 border border-destructive/40 text-destructive font-bold">
                  <AlertTriangle className="h-5 w-5" /> هذا الرابط <u>منتهي الصلاحية</u> ولن يعمل في المشغل.
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 border border-primary/30 text-primary font-bold">
                  <ShieldCheck className="h-5 w-5" /> الرابط صالح وآمن للتشغيل.
                </div>
              )}

              <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm p-4 rounded-lg bg-background border border-border">
                {data.title && (<>
                  <span className="text-muted-foreground">العنوان:</span>
                  <span className="font-bold truncate">{data.title}</span>
                </>)}
                {data.kind && (<>
                  <span className="text-muted-foreground">النوع:</span>
                  <span className="font-bold">{data.kind}</span>
                </>)}
                {data.slug && (<>
                  <span className="text-muted-foreground">المعرّف:</span>
                  <span className="font-mono text-xs">{data.slug}</span>
                </>)}
                {data.iat && (<>
                  <span className="text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> صدر:</span>
                  <span>{new Date(data.iat).toLocaleString("ar")}</span>
                </>)}
                {data.exp && (<>
                  <span className="text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> ينتهي:</span>
                  <span className={data.expired ? "text-destructive font-bold" : "font-bold"}>
                    {new Date(data.exp).toLocaleString("ar")}
                  </span>
                </>)}
                <span className="text-muted-foreground">الوجهة (مخفّاة):</span>
                <span className="font-mono text-xs break-all">{maskUrl(data.url)}</span>
              </div>

              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-primary">
                  إظهار الوجهة الكاملة (للأدمن فقط)
                </summary>
                <div className="mt-2 p-3 rounded bg-background border border-border font-mono text-[11px] break-all">
                  {data.url}
                </div>
              </details>

              {!data.expired && token && (
                <a
                  href={`/play?t=${encodeURIComponent(token)}${data.kind ? `&kind=${data.kind}` : ""}${data.slug ? `&slug=${data.slug}` : ""}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <Play className="h-4 w-4" /> فتح في المشغل <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
